import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Sparkles, 
  Video, 
  User, 
  Loader2, 
  Share2,
  Film,
  Trophy,
  Calendar,
  Copy,
  ExternalLink,
  ArrowLeft,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import TimelineEditor from '../components/TimelineEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SharedContent = () => {
  const { type, id } = useParams();
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const endpoint = type === 'reel' 
          ? `${API_URL}/api/share/reel/${id}`
          : `${API_URL}/api/share/analysis/${id}`;
        
        const res = await fetch(endpoint);
        if (res.ok) {
          setContent(await res.json());
        } else {
          setError('Content not found or has been removed');
        }
      } catch (err) {
        setError('Failed to load shared content');
      }
      setLoading(false);
    };

    fetchContent();
  }, [type, id]);

  const copyShareLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-rooam-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-rooam-blue animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading shared content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-rooam-black flex items-center justify-center">
        <Card className="bg-rooam-gray-medium border-rooam-gray-light max-w-md">
          <CardContent className="pt-6 text-center">
            <Sparkles className="h-12 w-12 text-gray-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Content Not Found</h2>
            <p className="text-gray-400 mb-4">{error}</p>
            <Button onClick={() => window.location.href = '/'} className="bg-rooam-blue text-black">
              <ArrowLeft className="h-4 w-4 mr-2" /> Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getIcon = () => {
    if (type === 'reel') return <Film className="h-6 w-6 text-rooam-blue" />;
    switch (content?.type) {
      case 'game_analysis': return <Video className="h-6 w-6 text-rooam-blue" />;
      case 'image_analysis': return <Sparkles className="h-6 w-6 text-rooam-blue" />;
      case 'game_log_analysis': return <Trophy className="h-6 w-6 text-rooam-blue" />;
      case 'player_insights': return <User className="h-6 w-6 text-rooam-blue" />;
      default: return <Sparkles className="h-6 w-6 text-rooam-blue" />;
    }
  };

  const getTitle = () => {
    if (type === 'reel') return content?.title || 'Highlight Reel';
    switch (content?.type) {
      case 'game_analysis': return 'Game Analysis';
      case 'image_analysis': return 'Image Analysis';
      case 'game_log_analysis': return content?.tournament_name || 'Tournament Analysis';
      case 'player_insights': return `${content?.athlete_name || 'Player'} Insights`;
      default: return 'Advanced Stats';
    }
  };

  return (
    <div className="min-h-screen bg-rooam-black">
      {/* Header */}
      <div className="bg-rooam-gray-medium border-b border-rooam-gray-light">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <a href="/" className="text-rooam-blue font-bold text-xl">ROOAM</a>
              <span className="text-gray-500">|</span>
              <span className="text-gray-400 text-sm">Shared Content</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="border-rooam-gray-light text-gray-300" onClick={copyShareLink}>
                <Copy className="h-4 w-4 mr-1" /> Copy Link
              </Button>
              <Button size="sm" className="bg-rooam-blue text-black" onClick={() => window.location.href = '/analysis'}>
                Create Your Own <ExternalLink className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Card className="bg-rooam-gray-medium border-rooam-gray-light">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white text-xl flex items-center gap-3">
                {getIcon()}
                {getTitle()}
              </CardTitle>
              <div className="flex items-center gap-2">
                {content?.sport && (
                  <Badge className="bg-rooam-blue/20 text-rooam-blue">{content.sport}</Badge>
                )}
                {content?.style && (
                  <Badge className="bg-purple-500/20 text-purple-400">{content.style}</Badge>
                )}
                {content?.created_at && (
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {new Date(content.created_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {type === 'reel' ? (
              <div className="space-y-4">
                {/* Reel Info */}
                <div className="flex items-center gap-4 mb-4">
                  {content?.duration && (
                    <Badge className="bg-rooam-blue/20 text-rooam-blue">{content.duration}s</Badge>
                  )}
                  {content?.style && (
                    <Badge className="bg-purple-500/20 text-purple-400">{content.style}</Badge>
                  )}
                </div>
                
                {/* Timeline Editor (read-only view) */}
                {content?.clips ? (
                  <TimelineEditor 
                    initialClips={content.clips}
                    duration={content.duration || 60}
                    style={content.style || 'hype'}
                    title={content.title || 'Highlight Reel'}
                    reelId={id}
                  />
                ) : (
                  <div className="bg-rooam-black rounded-lg p-6">
                    <h3 className="text-white font-semibold mb-3">Reel Script</h3>
                    <pre className="whitespace-pre-wrap text-sm text-gray-300 font-sans">{content?.script}</pre>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-rooam-black rounded-lg p-6">
                {content?.description && (
                  <div className="mb-4 pb-4 border-b border-rooam-gray-light">
                    <h4 className="text-sm text-gray-400 mb-1">Context</h4>
                    <p className="text-white">{content.description}</p>
                  </div>
                )}
                
                {content?.teams && content.teams.length > 0 && (
                  <div className="mb-4 pb-4 border-b border-rooam-gray-light">
                    <h4 className="text-sm text-gray-400 mb-1">Teams</h4>
                    <div className="flex gap-2">
                      {content.teams.map((team, i) => (
                        <Badge key={i} className="bg-rooam-gray-light text-white">{team}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {content?.athlete_name && (
                  <div className="mb-4 pb-4 border-b border-rooam-gray-light flex items-center gap-3">
                    <img 
                      src={content.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${content.athlete_id}`} 
                      alt={content.athlete_name}
                      className="w-12 h-12 rounded-full"
                    />
                    <div>
                      <h4 className="text-white font-semibold">{content.athlete_name}</h4>
                      <p className="text-sm text-gray-400">{content.position} • {content.team}</p>
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="text-sm text-gray-400 mb-2 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" /> Analysis
                  </h4>
                  <pre className="whitespace-pre-wrap text-sm text-gray-300 font-sans">
                    {content?.analysis || content?.insights}
                  </pre>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="mt-8 text-center">
          <p className="text-gray-400 mb-4">Want AI-powered analysis for your games?</p>
          <Button size="lg" className="bg-rooam-blue text-black" onClick={() => window.location.href = '/analysis'}>
            <Sparkles className="h-5 w-5 mr-2" /> Try Rooam Advanced Stats
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SharedContent;
